import {
  useGetCardStatementQuery,
  useLazyDownloadCardStatementQuery,
} from "@/modules/card/services";
import { DownloadExtension } from "@/shared/common/constants";
import { DownloadType } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { FilterPanel, Statement } from "@/shared/components";
import { useFilters, useNotify } from "@/shared/hooks";
import { downloadFileFromUrl } from "@/shared/utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export const CardStatement = () => {
  const { t } = useTranslation();
  const { cardId = "" } = useParams();
  const { showErrorSnackbar } = useNotify();

  const { filters, setFilters, defaultFilters } = useFilters();

  const [
    downloadCardStatement,
    { isFetching: isDownloading },
  ] = useLazyDownloadCardStatementQuery();

  const {
    data: cardStatement,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetCardStatementQuery({
    id: cardId,
    ...filters,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const onDownload = (exportFormat: DownloadType) => {
    downloadCardStatement({
      id: cardId,
      exportFormat,
      ...filters,
    })
      .unwrap()
      .then((response) => {
        if (response?.url) {
          const fileType = DownloadExtension[exportFormat];
          const name = `Card-statements-${filters.startDate}-${filters.endDate}.${fileType}`;
          downloadFileFromUrl(response.url, name);
        }
      })
      .catch((error) => {
        showErrorSnackbar(error);
      });
  };

  const actions = [
    {
      label: t("general.pdf"),
      type: DownloadType.Pdf,
    },
    {
      label: t("general.cb"),
      type: DownloadType.Excel,
    }
  ];

  return (
    <Statement
      onDownload={onDownload}
      isFetching={isFetching}
      isError={isError}
      actions={actions}
      isDownloading={isDownloading}
      error={error as IError}
      refetch={refetch}
      statement={cardStatement}
    >
      <FilterPanel
        filters={filters}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
    </Statement>
  );
};
